import React, { useMemo, useState } from 'react';
import { Download, Loader2, Upload } from 'lucide-react';
import { bulkCreateOrgs, downloadOrgTemplate } from './api';
import type { BulkRowResult, OrgsMeta } from './types';
import { helperClass, primaryBtnClass, secondaryBtnClass } from '../styles';

interface BulkCreateOrganizationsProps {
  meta: OrgsMeta;
}

const BulkCreateOrganizations: React.FC<BulkCreateOrganizationsProps> = ({ meta }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkRowResult[] | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [previewed, setPreviewed] = useState(false);

  const errorCount = useMemo(
    () => (results || []).filter((r) => r.status === 'error').length,
    [results]
  );

  const onFile = async (file: File | null) => {
    setResults(null);
    setSummary(null);
    setPreviewed(false);
    setError(null);
    if (!file) {
      setFileName(null);
      setCsv(null);
      return;
    }
    setFileName(file.name);
    setCsv(await file.text());
  };

  const run = async (dryRun: boolean) => {
    if (!csv) {
      setError('Choose a CSV file first.');
      return;
    }
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const payload = await bulkCreateOrgs(csv, dryRun);
      setResults(payload.results);
      setPreviewed(true);
      if (dryRun) {
        setSummary(
          payload.errors
            ? `Preview finished with ${payload.created} ready and ${payload.errors} with problems.`
            : `Preview looks good: ${payload.created} organization${payload.created === 1 ? '' : 's'} ready to create.`
        );
      } else {
        setSummary(
          `Imported ${payload.created} organization${payload.created === 1 ? '' : 's'}` +
            (payload.errors ? `, ${payload.errors} skipped with errors.` : '.')
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-[#626262] mb-4">
        Import several organizations from a CSV file. Download the template first.
      </p>
      <div className="rounded-lg border border-[#E8E8E8] bg-[#FAFAFA] px-4 py-3 text-[13px] text-[#4A4A4A] mb-4 space-y-1">
        <p>
          <span className="font-medium">Required:</span> name, tier, currency, account manager email.
        </p>
        <p>
          <span className="font-medium">Optional:</span> org admin email.
        </p>
        <p>
          <span className="font-medium">Tiers:</span>{' '}
          {meta.tiers.map((t) => t.name).join(', ') || '—'}
        </p>
        <p>
          <span className="font-medium">Currencies:</span>{' '}
          {meta.currencies.map((c) => c.code).join(', ') || '—'}
        </p>
        <p className={helperClass}>
          Account manager must already exist and have the organizations_account_manager
          capability.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => void downloadOrgTemplate().catch((err) => setError(err.message))}
          className={secondaryBtnClass}
        >
          <Download className="w-4 h-4 inline mr-1.5" />
          Download template
        </button>
        <label className={`${secondaryBtnClass} cursor-pointer inline-flex items-center`}>
          <Upload className="w-4 h-4 inline mr-1.5" />
          {fileName || 'Choose CSV'}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => void onFile(e.target.files?.[0] || null)}
          />
        </label>
        <button
          type="button"
          disabled={busy || !csv}
          onClick={() => void run(true)}
          className={secondaryBtnClass}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />}
          Preview
        </button>
        <button
          type="button"
          disabled={busy || !csv || !previewed}
          onClick={() => void run(false)}
          className={primaryBtnClass}
        >
          Import
        </button>
      </div>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {summary && (
        <div
          className={`mb-4 rounded-md px-3 py-2 text-sm ${
            errorCount ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {summary}
        </div>
      )}
      {results && (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Row</th>
                <th className="px-3 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={`${row.row}-${row.name}`} className="border-t border-[#E8E8E8]">
                  <td className="px-3 py-2">{row.row ?? '—'}</td>
                  <td className="px-3 py-2">{row.name || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.status}</td>
                  <td className={`px-3 py-2 ${row.status === 'error' ? 'text-red-700' : ''}`}>
                    {row.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkCreateOrganizations;
